/// src/composables/useRoleCaps.js
// ENHANCED VERSION with proper claims validation and token refresh

import { ref, computed, onMounted } from 'vue'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

const ROLE_ADMIN = 'ADMIN'
const ROLE_COACH = 'RefereeCoach'
const ROLE_REF   = 'Referee'

function useRoleCaps() {
  const auth = getAuth()

  const role = ref(null)           // custom claim: role
  const org = ref(null)            // custom claim: org or organization
  const userId = ref(null)
  const claimsReady = ref(false)
  const rolesMap = ref({})         // ✅ NEW: Store all org-role mappings

  async function readClaims(user, force = false) {
    claimsReady.value = false
    try {
      // ✅ ENHANCED: Always force refresh to get latest claims
      const idt = await user.getIdTokenResult(force || true)
      const c = idt.claims || {}
      
      // ✅ NEW: Store the roles map for multi-org support
      rolesMap.value = c.roles || {}
      
      // Active role and org
      role.value = c.role || localStorage.getItem('currentUserRole') || null
      org.value  = c.org || c.organization || localStorage.getItem('currentUserOrgName') || null
      userId.value = user.uid

      // ✅ NEW: Validate that stored org/role match claims
      if (org.value && role.value) {
        const actualRoleForOrg = rolesMap.value[org.value]
        if (actualRoleForOrg && actualRoleForOrg !== role.value) {
          console.warn(`Role mismatch detected: stored="${role.value}", actual="${actualRoleForOrg}"`)
          role.value = actualRoleForOrg
          localStorage.setItem('currentUserRole', actualRoleForOrg)
        }
      }

      // ✅ NEW: Check token expiration
      const expirationTime = new Date(idt.expirationTime)
      const now = new Date()
      const timeUntilExpiry = expirationTime - now
      
      if (timeUntilExpiry < 5 * 60 * 1000) {
        console.log('Token expiring soon, will refresh on next call')
      }

    } catch (e) {
      console.warn('useRoleCaps/readClaims:', e)
      role.value = localStorage.getItem('currentUserRole') || null
      org.value  = localStorage.getItem('currentUserOrgName') || null
      userId.value = user?.uid || null
    } finally {
      claimsReady.value = true
    }
  }

  onMounted(() => {
    const current = auth.currentUser
    if (current) readClaims(current, true)

    const un = onAuthStateChanged(auth, (u) => {
      if (!u) {
        role.value = null
        org.value = null
        userId.value = null
        rolesMap.value = {}
        claimsReady.value = true
        return
      }
      readClaims(u, true)
    })
  })

  const isAdmin    = computed(() => role.value === ROLE_ADMIN)
  const isRefCoach = computed(() => role.value === ROLE_COACH)
  const isReferee  = computed(() => role.value === ROLE_REF)

  // ✅ NEW: Check if user has access to specific organization
  const hasAccessToOrg = computed(() => (orgName) => {
    return !!rolesMap.value[orgName]
  })

  // ✅ NEW: Get all organizations user has access to
  const availableOrgs = computed(() => Object.keys(rolesMap.value))

  // ✅ NEW: Get role for specific organization
  const getRoleForOrg = computed(() => (orgName) => {
    return rolesMap.value[orgName] || null
  })

  // Capabilities per role (tweak as needed)
  const caps = computed(() => {
    if (isAdmin.value) {
      return {
        viewAllReports: true,
        editAnyReport:  true,
        createReport:   true,
        deleteMisconduct: true,
        manageUsers:    true,
        canEditAnyMisconduct: true, // ✅ NEW: For misconduct editing
      }
    }
    if (isRefCoach.value) {
      return {
        viewAllReports: true,
        editAnyReport:  true,
        createReport:   false,
        deleteMisconduct: false,
        manageUsers:    false,
        canEditAnyMisconduct: true, // ✅ NEW: Coaches can edit any misconduct
      }
    }
    // Referee (default)
    return {
      viewAllReports: false,
      editAnyReport:  false,
      createReport:   true,
      deleteMisconduct: false,
      manageUsers:    false,
      canEditAnyMisconduct: false, // ✅ NEW: Referees can only edit their own
    }
  })

  function can(capability) {
    return !!caps.value[capability]
  }

  async function refresh() {
    const u = auth.currentUser
    if (u) await readClaims(u, true)
  }

  // ✅ NEW: Validate user has access to current session
  async function validateSession() {
    const u = auth.currentUser
    if (!u) {
      throw new Error('Not authenticated')
    }

    await readClaims(u, true)

    const storedOrg = localStorage.getItem('currentUserOrgName')
    const storedRole = localStorage.getItem('currentUserRole')

    if (!storedOrg || !storedRole) {
      throw new Error('Session data missing')
    }

    if (!rolesMap.value[storedOrg]) {
      throw new Error(`No access to organization: ${storedOrg}`)
    }

    return true
  }

  return {
    // identity / claims
    role, org, userId, claimsReady, rolesMap,
    // role helpers
    isAdmin, isRefCoach, isReferee,
    // ✅ NEW: organization helpers
    hasAccessToOrg, availableOrgs, getRoleForOrg,
    // capabilities
    caps, can,
    // utils
    refresh, validateSession,
  }
}

export default useRoleCaps
export { useRoleCaps }